import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { DatapointFieldActions } from './datapoint-field.actions';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { PermissionService } from '../../features/shared/services/permission.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointFieldService } from '../../features/unified-datapoint/services/datapoint-field.service';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class DatapointFieldEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly service = inject(DatapointFieldService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);

  loadDatapointFieldEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.LoadDatapointField),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getById(action.id).pipe(
          map((field) => DatapointFieldActions.LoadDatapointFieldSuccess({ field })),
          reportEffectError(DatapointFieldActions.LoadDatapointFieldFail)
        )
      )
    )
  );

  createDatapointFieldEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.CreateDatapointField),
      withLatestFrom(
        this.store,
        this.permissionService.hasEveryPermission([Permission.TEMPLATE_USER])
      ),
      switchMap(([action, store, hasTemplatePermissions]) =>
        this.service.create(action.field, true, hasTemplatePermissions).pipe(
          map((field) => DatapointFieldActions.CreateDatapointFieldSuccess({ field })),
          reportEffectError(DatapointFieldActions.CreateDatapointFieldFail)
        )
      )
    )
  );

  updateDatapointFieldEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.UpdateDatapointField),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(
            { ...action.field, id: action.field.id || store?.datapointField?.datapointField?.id },
            true,
            !!action.field?.canShare,
            !!action.field?.canPublish
          )
          .pipe(
            map((field) => DatapointFieldActions.UpdateDatapointFieldSuccess({ field })),
            reportEffectError(DatapointFieldActions.UpdateDatapointFieldFail)
          )
      )
    )
  );

  createDatapointFieldSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.CreateDatapointFieldSuccess),
      map(() =>
        AppActions.PushNotification({
          message: 'Field created',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  updateDatapointFieldSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.UpdateDatapointFieldSuccess),
      map(() =>
        AppActions.PushNotification({
          message: 'Field updated',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  createDatapointFieldSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatapointFieldActions.CreateDatapointFieldSuccess),
        map((action) =>
          this.router.navigate([
            '/',
            SemerisRoutes.datapoint.root,
            SemerisRoutes.datapoint.field.root,
            'user',
            action.field?.id,
          ])
        )
      ),
    { dispatch: false }
  );

  createDatapointFieldFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.CreateDatapointFieldFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to create the field',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  updateDatapointFieldFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointFieldActions.UpdateDatapointFieldFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update the field',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  notFoundRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatapointFieldActions.LoadDatapointFieldFail),
        map(() => this.router.navigate(['/', SemerisRoutes.datapoint.root]))
      ),
    { dispatch: false }
  );

  templateModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      map((action) =>
        action.modalId === 'edit-datapoint-field' && !!action.data
          ? { data: action.data, additionalInfo: action.additionalInfo }
          : {}
      ),
      filter(({ data }) => !!data),
      withLatestFrom(this.store),
      map(([{ data, additionalInfo }, store]) => {
        const field = store.datapointFieldList.fields.find((f) => f.id === data.id);
        return DatapointFieldActions.UpdateDatapointField({
          field: {
            ...field,
            ...data,
          },
        });
      })
    )
  );
}
